
/**
 * Polls a function until it returns a truthy value.
 * 
 * @param {Object} options - The options object.
 * @param {Function} options.isComplete - Required.
 *   The function to poll. Must return a truthy value when complete.
 * @param {Integer} options.startingInterval - Defaults to 1.
 *   The starting interval at which to poll in seconds.
 * @param {Integer} options.decayCount - Defaults to 5.
 *   Every `decayCount` polls, the interval will be increased by `decayIncrement`.
 * @param {Integer} options.decayIncrement - Defaults to 1.
 *   The amount to increase the interval by every `decayCount` polls.
 * @param {Integer} options.maxDuration - Default value is 60.
 *   The maximum duration in seconds to poll before giving up and resolving with false.
 * 
 * @returns {Promise<Boolean>} - Resolves either true (if the function returned a truthy value) or
 *   false (if the max duration was reached).
 */
export const pollUntil = async ({
  isComplete,
  startingInterval = 1,
  decayCount = 5,
  decayIncrement = 1,
  maxDuration = 60,
}) => {
  let currentInterval = startingInterval * 1000;
  let elapsedTime = 0;
  let pollCount = 0;

  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const result = await isComplete();
        if (result) return resolve(true);

        elapsedTime += currentInterval;
        pollCount++;

        if (elapsedTime >= (maxDuration * 1000)) return resolve(false);

        if (pollCount % decayCount === 0) {
          currentInterval += decayIncrement * 1000;
        }

        setTimeout(poll, currentInterval);
      } catch (error) {
        reject(error);
      }
    };

    poll();
  });
};
